import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { FC, KeyboardEvent, useEffect, useState } from "react";
import {
  InventoryPlannedProducts,
  ProductTypesEnum,
  PlanTypes,
  Status,
} from "../../../../types/inventoryData";
import { areIntervalsOverlapping } from "date-fns";
import { AddRounded, CloseRounded, RemoveRounded } from "@mui/icons-material";
import { useInventory } from "../../InventoryProvider";
import {
  AddInventoryPlan,
  DeleteInventoryPlan,
  UpdateInventoryPlan,
} from "../../../../api/inventory";
import { useApplicationContext } from "../../../ContextProviders/ApplicationContext";
import { ErrorCodes } from "../../../../types/enums";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { dateTimeLocales } from "../../../../helpers/utils";
import { useTranslation } from "react-i18next";

type GanttEditTypes = {
  editPopup: boolean;
  handleClose: () => void;
  editPlan: PlanTypes | undefined;
  statusCounts: Record<Status, Record<ProductTypesEnum, number>>;
};

const GanttEdit: FC<GanttEditTypes> = ({
  editPopup,
  handleClose,
  editPlan,
  statusCounts,
}) => {
  const { plans, setPlans, productTypes } = useInventory();
  const { t, i18n } = useTranslation();
  const { showSnackbar } = useApplicationContext();
  const convertProductTypes: InventoryPlannedProducts[] = productTypes.map(
    (p) => {
      return { productType: { ...p }, quantity: 0 };
    }
  );
  const [confirmCloseBoolean, setConfirmCloseBoolean] =
    useState<boolean>(false);
  const [confirmDelete, setConfirmDelete] = useState<boolean>(false);
  const [available, setAvailable] = useState<InventoryPlannedProducts[]>();
  const [convertedAllocatedProducts, setConvertedAllocatedProducts] =
    useState<Map<string, number>>();

  const calcAvailable = () => {
    const otherPlans = plans.filter(
      (p) => p.inventoryPlanId !== editPlan?.inventoryPlanId
    );

    const overlappingPlans = otherPlans.filter((p) => {
      const planStartDate = p.startDate;
      const planEndDate = p.endDate;

      return areIntervalsOverlapping(
        {
          start: new Date(getValues("startDate")),
          end: new Date(getValues("endDate")),
        },
        { start: new Date(planStartDate), end: new Date(planEndDate) }
      );
    });

    const allPlanProducts = overlappingPlans.map((p) => p.plannedProducts);

    let arr: InventoryPlannedProducts[] = [];
    Object.entries(statusCounts[Status.Available]).map(([key, value]) => {
      const productTypeFound = productTypes.find((p) => p.name === key);
      if (productTypeFound)
        arr.push({ productType: productTypeFound, quantity: value });
      else return;
    });

    allPlanProducts.forEach((singlePlannedProduct) => {
      singlePlannedProduct.map((item) => {
        const foundItem = arr.find((i) => i.productType == item.productType);
        if (foundItem) {
          const index = arr.findIndex((i) => i.productType == item.productType);
          const changedValues: InventoryPlannedProducts = {
            productType: item.productType,
            quantity: foundItem.quantity - item.quantity,
          };
          arr[index] = changedValues;
        }
      });
    });

    setAvailable(arr);
  };

  useEffect(() => {
    calcAvailable();
    const allocated = getValues("allocatedProducts");

    if (allocated == null) return;

    const supermap = new Map<string, number>();

    productTypes.forEach((e) => {
      supermap.set(e.name, 0);
    });

    allocated.forEach((i) => {
      let value = supermap.get(i.productType.name);
      if (value == undefined) value = 0;
      supermap.set(i.productType.name, value + 1);
    });

    setConvertedAllocatedProducts(supermap);
  }, []);

  const handleSave: SubmitHandler<PlanTypes> = async (data) => {
    if (getValues("inventoryPlanId")) {
      try {
        const updatedPlan: PlanTypes = await UpdateInventoryPlan(data);
        const changedPlans = plans.map((p) =>
          p.inventoryPlanId == updatedPlan.inventoryPlanId ? updatedPlan : p
        );
        setPlans(changedPlans);
        showSnackbar(t("inventory.successupdate"), "success");
        handleClose();
      } catch (error) {
        if (error instanceof Error) {
          switch (error.message) {
            case ErrorCodes.EmptyPlannedProductsOnPlan:
              showSnackbar(t("errors.EmptyPlannedProductsOnPlan"), "error");
              break;
            case ErrorCodes.PlanNotFound:
              showSnackbar(t("errors.PlanNotFound"), "error");
              break;
            case ErrorCodes.InternalServerError:
              showSnackbar(t("errors.UnexpectedError"), "error");
          }
        }
        showSnackbar(t("errors.UnexpectedError"), "error");
      }
    } else {
      try {
        const newPlan: PlanTypes = await AddInventoryPlan(data);
        setPlans([...plans, newPlan]);
        showSnackbar(t("inventory.successfullyAdded"), "success");
        handleClose();
      } catch (error) {
        if (error instanceof Error) {
          switch (error.message) {
            case ErrorCodes.EmptyProductTypeList:
              showSnackbar(t("errors.EmptyProductTypeList"), "error");
              break;
            case ErrorCodes.EmptyPlannedProductsOnPlan:
              showSnackbar(t("errors.EmptyPlannedProductsOnPlan"), "error");
              break;
            case ErrorCodes.ProductTypeNotFound:
              showSnackbar(t("errors.ProductTypeNotFound"), "error");
              break;
            default:
              showSnackbar(t("errors.UnexpectedError"), "error");
              break;
          }
        } else {
          showSnackbar(t("errors.UnexpectedError"), "error");
        }
      }
    }
  };

  const handleClosingChecker = () => {
    if (isDirty == true) {
      setConfirmCloseBoolean(true);
      return;
    }
    
    handleClose();
  };

  const handleKeyPress = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter") {
      handleSubmit(handleSave);
    }
  };

  const handleDeletePlan: SubmitHandler<PlanTypes> = (data) => {
    try {
      DeleteInventoryPlan(data);
      setPlans((prev) =>
        prev.filter((p) => p.inventoryPlanId !== editPlan?.inventoryPlanId)
      );
      setConfirmDelete(true);
      handleClose();
    } catch (error) {
      if (error instanceof Error) {
        switch (error.message) {
          case ErrorCodes.NoDeletedRows:
            showSnackbar(t("errors.NoDeletedRows"), "error");
            break;
          case ErrorCodes.ManyDeletedRows:
            showSnackbar(t("errors.UnexpectedError"), "error");
            break;
          case ErrorCodes.InternalServerError:
            showSnackbar(t("errors.UnexpectedError"), "error");
            break;
          default:
            showSnackbar(t("errors.UnexpectedError"), "error");
            break;
        }
      }
      showSnackbar(t("errors.UnexpectedError"), "error");
    }
  };

  const {
    handleSubmit,
    control,
    formState: { isDirty },
    watch,
    setValue,
    getValues,
  } = useForm<PlanTypes>({
    defaultValues: {
      inventoryPlanId: editPlan?.inventoryPlanId,
      companyName: editPlan?.companyName || "",
      startDate: editPlan?.startDate
        ? new Date(editPlan.startDate)
        : new Date(Date.now()),
      endDate: editPlan?.endDate
        ? new Date(editPlan.endDate)
        : new Date(Date.now()),
      plannedProducts: editPlan?.plannedProducts || convertProductTypes,
      allocatedProducts: editPlan?.allocatedProducts || [],
    },
    mode: "onBlur",
  });

  return (
    <>
      <Dialog
        open={confirmCloseBoolean}
        onClose={() => setConfirmCloseBoolean(false)}
      >
        <DialogTitle>{t("inventory.youHaveUnsavedChanges")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("inventory.closingThisDialogWillDiscardYourChanges")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            autoFocus
            variant="contained"
            onClick={() => setConfirmCloseBoolean(false)}
          >
            {t("buttons.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setConfirmCloseBoolean(true);
              handleClose();
            }}
          >
            {t("inventory.closeAndDiscard")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <form onSubmit={handleSubmit(handleDeletePlan)}>
          <DialogTitle>{t("inventory.confirmPlanDeletion")}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t("inventory.thisActionCannotBeReverted")}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              autoFocus
              variant="contained"
              onClick={() => setConfirmDelete(false)}
            >
              {t("buttons.cancel")}
            </Button>
            <Button variant="contained" type="submit">
              {t("inventory.continueAndDelete")}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog open={editPopup} onClose={handleClosingChecker}>
        <form onSubmit={handleSubmit(handleSave)}>
          <Box display="flex" flexDirection="column" padding={2} gap={2}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <DialogTitle sx={{ padding: 0 }}>{t("inventory.editPlan")}</DialogTitle>
              <IconButton size="large" onClick={handleClosingChecker}>
                <CloseRounded />
              </IconButton>
            </Box>

            <Controller
              name="companyName"
              control={control}
              rules={{
                required: { value: true, message: t("errors.required") },
              }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  error={!!error}
                  required
                  helperText={error?.message}
                  variant="filled"
                  label={t("inventory.planName")}
                  onKeyDown={(event) => handleKeyPress(event)}
                />
              )}
            />

            <Box display={"flex"} gap={2}>
              <LocalizationProvider
                dateAdapter={AdapterDateFns}
                adapterLocale={dateTimeLocales[i18n.language]}
              >
                <Controller
                  name="startDate"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <DatePicker
                      {...field}
                      sx={{ width: "100%" }}
                      label={t("inventory.startDate")}
                      showDaysOutsideCurrentMonth
                      displayWeekNumber
                      onChange={(e) => {
                        field.onChange(e);
                        calcAvailable();
                      }}
                    />
                  )}
                />
                <Controller
                  name="endDate"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <DatePicker
                      {...field}
                      sx={{ width: "100%" }}
                      label={t("inventory.endDate")}
                      showDaysOutsideCurrentMonth
                      displayWeekNumber
                      minDate={watch("startDate")}
                      onChange={(e) => {
                        field.onChange(e);
                        calcAvailable();
                      }}
                    />
                  )}
                />
              </LocalizationProvider>
            </Box>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t("labels.product")}</TableCell>
                    <TableCell>{t("labels.amountPlanned")}</TableCell>
                    <TableCell>{t("labels.availableInTimeframe")}</TableCell>
                    <TableCell>{t("labels.allocated")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {productTypes.map((item, index) => (
                    <TableRow key={item.productTypeId}>
                      <TableCell>{item.name}</TableCell>
                      <TableCell
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <Controller
                          name={`plannedProducts.${index}.quantity`}
                          control={control}
                          rules={{
                            required: {
                              value: true,
                              message: `${t("inventory.amountIsRequired")}`,
                            },
                            pattern: {
                              value: /^\d+$/,
                              message: `${t("inventory.onlyNumbers")}`,
                            },
                            min: {
                              value: 0,
                              message: `${t("inventory.numberMustBePositive")}`,
                            },
                          }}
                          render={({ field, fieldState: { error } }) => (
                            <TextField
                              {...field}
                              error={!!error}
                              helperText={error?.message}
                              onBlur={(e) => {
                                if (!e.target.value) {
                                  setValue(
                                    `plannedProducts.${index}.quantity`,
                                    0,
                                    { shouldDirty: true, shouldValidate: true }
                                  );
                                }
                                field.onBlur();
                              }}
                            />
                          )}
                        />

                        <IconButton
                          size="large"
                          sx={{ height: "100%" }}
                          onClick={() => {
                            const isNumber = /^\d+$/.test(
                              getValues(
                                `plannedProducts.${index}.quantity`
                              ).toString()
                            );

                            const isPositive =
                              getValues(`plannedProducts.${index}.quantity`) -
                                1 >=
                              0;

                            if (isNumber && isPositive) {
                              setValue(
                                `plannedProducts.${index}.quantity`,
                                parseInt(
                                  getValues(
                                    `plannedProducts.${index}.quantity`
                                  ).toString()
                                ) - 1,
                                { shouldDirty: true, shouldValidate: true }
                              );
                            }
                          }}
                        >
                          <RemoveRounded />
                        </IconButton>

                        <IconButton
                          size="large"
                          sx={{ height: "100%" }}
                          onClick={() => {
                            const isNumber = /^\d+$/.test(
                              getValues(
                                `plannedProducts.${index}.quantity`
                              ).toString()
                            );
                            const isPositive =
                              getValues(`plannedProducts.${index}.quantity`) +
                                1 >=
                              0;

                            if (isNumber && isPositive) {
                              setValue(
                                `plannedProducts.${index}.quantity`,
                                parseInt(
                                  getValues(
                                    `plannedProducts.${index}.quantity`
                                  ).toString()
                                ) + 1,
                                { shouldDirty: true, shouldValidate: true }
                              );
                            }
                          }}
                        >
                          <AddRounded />
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        {available &&
                          available.find((i) => i.productType.name == item.name)
                            ?.quantity}
                      </TableCell>
                      <TableCell>
                        {convertedAllocatedProducts?.get(item.name)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box display={"flex"} justifyContent={"space-between"}>
              {editPlan?.plannedProducts && (
                <Button
                  variant="contained"
                  onClick={() => {
                    setConfirmDelete(true);
                  }}
                >
                  {t("labels.delete")}
                </Button>
              )}

              <Box display={"flex"} gap={2} ml={"auto"}>
                <Button variant="contained" onClick={handleClosingChecker}>
                  {t("buttons.cancel")}
                </Button>
                <Button variant="contained" type="submit">
                  {t("buttons.save")}
                </Button>
              </Box>
            </Box>
          </Box>
        </form>
      </Dialog>
    </>
  );
};

export default GanttEdit;
